import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { file_path } from "./environment";
import axios from "axios";

import SelectGuide from './SelectGuide.js';

function LawyerResults({ results }) {
    return (
        <div className="autocomplete-results" role="listbox">
            {results.map((lawyer) => (
                <div key={lawyer.id}>
                    <Link className="autocomplete-option" key={lawyer.id} role="option" to={`/editorial/lawyers/${lawyer.id}`}>
                        {lawyer.name} - {lawyer.firm}
                    </Link>
                </div>
            ))}
        </div>
    );
}

function FirmResults({ results }) {
    return (
        <div className="autocomplete-results" role="listbox">
            {results.map((firm) => (
                <Link className="autocomplete-option" key={firm.id} role="option" to={`/editorial/firms/${firm.id}`}>
                    {firm.name}
                </Link>
            ))}
        </div>
    );
}

export default function Editorial() {

    const [guides, setGuides] = useState([]);
    const [guide, setGuide] = useState({});
    const [lawyers, setLawyers] = useState([]);
    const [lawyerQuery, setLawyerQuery] = useState("");
    const [lawyerResults, setLawyerResults] = useState([]);
    const [firms, setFirms] = useState([]);
    const [firmQuery, setFirmQuery] = useState("");
    const [firmResults, setFirmResults] = useState([]);
    const lawyerCount = lawyers.length;
    const firmCount = firms.length;

    const guideSelected = useCallback((newGuide) => {
        if (!newGuide) {
            return;
        }

        setGuide(newGuide);
        setFirmQuery("");
        setLawyerQuery("");
        axios.get(file_path(`/lawyers-list/${newGuide.id}.json`)).then((res) => {
            setLawyers(res.data.lawyers);
        });
        axios.get(file_path(`/firms-list/${newGuide.id}.json`)).then((res) => {
            setFirms(res.data.firms);
        });
    });

    function findGuideById(guideList, guideId = 5) {
        return guideList.find((element) => element.id.toString() === guideId.toString());
    }

    useEffect(() => {
        document.title = "Editorial";
        axios.get(file_path('/guides.json')).then((res) => {
            setGuides(res.data.guides);
            const found = findGuideById(res.data.guides);
            if (found) {
                guideSelected(found);
            }
        });
    }, []);

    function findLawyerByName(text) {
        const searchText = text.toLowerCase();
        return lawyers
            .filter((element) => element.name.toLowerCase().includes(searchText))
            .slice(0, 10);
    }

    function onLawyerChange(event) {
        const text = event.target.value;
        setLawyerQuery(text);

        if (!text.trim()) {
            setLawyerResults([]);
            return;
        }

        const matches = findLawyerByName(text);
        setLawyerResults(matches);
    }

    function findFirmByName(text) {
        const searchText = text.toLowerCase();
        return firms
            .filter((element) => element.name.toLowerCase().includes(searchText))
            .slice(0, 10);
    }

    function onFirmChange(event) {
        const text = event.target.value;
        setFirmQuery(text);

        if (!text.trim()) {
            setFirmResults([]);
            return;
        }

        const matches = findFirmByName(text);
        setFirmResults(matches);
    }

    return (
        <main className="firm-editorial-page editorial-page">
            <section className="hero firm-hero">
                <div className="firm-hero__eyebrow">Editorial search</div>
                <h1>Browse Editorial History</h1>
                <p className="firm-hero__summary">
                    Search lawyers and firms by guide, then jump straight to the editorial pages for rankings, feedback, and notable practitioners.
                </p>
                <div className="firm-hero__stats">
                    <div className="firm-hero__stat">
                        <span className="firm-meta-label">Guides loaded</span>
                        <span className="firm-meta-value">{guides.length}</span>
                    </div>
                    <div className="firm-hero__stat">
                        <span className="firm-meta-label">Lawyers</span>
                        <span className="firm-meta-value">{lawyerCount}</span>
                    </div>
                    <div className="firm-hero__stat">
                        <span className="firm-meta-label">Firms</span>
                        <span className="firm-meta-value">{firmCount}</span>
                    </div>
                </div>
            </section>

            <section className="editorial-grid">
                <section className="firm-section-card">
                    <div className="firm-section-header">
                        <div>
                            <p className="firm-section-kicker">Choose a guide</p>
                            <h2>Current guide</h2>
                            <p className="firm-section-location">Switch between editorial guides before searching.</p>
                        </div>
                    </div>
                    <div className="editorial-guide-select">
                        <SelectGuide guides={guides} value={guide} guideSelected={guideSelected} />
                    </div>
                </section>

                <section className="firm-section-card">
                    <div className="firm-section-header">
                        <div>
                            <p className="firm-section-kicker">Lawyers</p>
                            <h2>Search lawyers</h2>
                            <p className="firm-section-location">Find a lawyer and open the editorial profile.</p>
                        </div>
                    </div>
                    <div className="autocomplete editorial-autocomplete">
                        <input
                            aria-autocomplete="list"
                            autoComplete="off"
                            className="autocomplete-input"
                            type="text"
                            placeholder="Search for lawyers..."
                            value={lawyerQuery}
                            onChange={onLawyerChange}
                        />
                        {lawyerQuery.trim() && lawyerResults.length > 0 ? <LawyerResults results={lawyerResults} /> : null}
                    </div>
                </section>

                <section className="firm-section-card">
                    <div className="firm-section-header">
                        <div>
                            <p className="firm-section-kicker">Firms</p>
                            <h2>Search firms</h2>
                            <p className="firm-section-location">Find the firm profile and see its ranked sections.</p>
                        </div>
                    </div>
                    <div className="autocomplete editorial-autocomplete">
                        <input
                            aria-autocomplete="list"
                            autoComplete="off"
                            className="autocomplete-input"
                            type="text"
                            placeholder="Search for firms..."
                            value={firmQuery}
                            onChange={onFirmChange}
                        />
                        {firmQuery.trim() && firmResults.length > 0 ? <FirmResults results={firmResults} /> : null}
                    </div>
                </section>
            </section>
        </main>
    );
}